import { type AnalystsReply, API_PATHS, analystsReplySchema } from '@alphadesk/web/contract.ts';
import { useCallback, useEffect, useRef, useState } from 'react';
import { usageSession } from './posthog.ts';

export interface Parser<T> {
  safeParse(raw: unknown): { success: true; data: T } | { success: false };
}

export type ReadState<T> =
  | { status: 'loading' }
  | { status: 'ok'; data: T }
  | { status: 'error'; message: string };

export const SIGNED_OUT = new Error('Signed out.');

/** Every call to the bot goes through here. A signed-out answer sends the browser to sign in and throws `SIGNED_OUT`. */
export async function send(
  path: string,
  {
    method = 'GET',
    body,
    signal,
  }: { method?: 'GET' | 'POST' | 'DELETE'; body?: unknown; signal?: AbortSignal } = {},
): Promise<Response> {
  // Lets server logs be joined to the (masked) session recording. Omitted when not recording.
  const usage = usageSession();
  const response = await fetch(path, {
    method,
    credentials: 'same-origin',
    headers: {
      ...(body === undefined ? {} : { 'content-type': 'application/json' }),
      ...(usage ? { 'x-usage-session': usage } : {}),
    },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    ...(signal ? { signal } : {}),
  });
  if (response.status === 401) {
    window.location.assign('/');
    throw SIGNED_OUT;
  }
  return response;
}

export async function readJson<T>(
  path: string,
  schema: Parser<T>,
  signal?: AbortSignal,
): Promise<T> {
  const response = await send(path, signal ? { signal } : {});
  if (!response.ok) throw new Error(`The bot answered ${response.status}.`);
  const parsed = schema.safeParse(await response.json());
  if (!parsed.success) throw new Error('The bot answered something this build cannot read.');
  return parsed.data;
}

const LOADING = { status: 'loading' } as const;

export interface Read<T> {
  state: ReadState<T>;
  checking: boolean;
  refresh: () => void;
}

/**
 * A null path asks nothing. A new ask aborts the one in flight, so an older answer cannot land
 * last. `every` asks again every so many ms while the tab is in view, for facts that change
 * elsewhere with no event (a Kite login, a fallback stop moved in Kite).
 */
export function useRead<T>(
  path: string | null,
  schema: Parser<T>,
  { every }: { every?: number } = {},
): Read<T> {
  // Tagged with its path, so a read of another path never shows this one's answer.
  const [held, setHeld] = useState<{ of: string | null; state: ReadState<T> }>({
    of: path,
    state: LOADING,
  });
  const [checking, setChecking] = useState(path !== null);
  const inFlight = useRef<AbortController | null>(null);

  const refresh = useCallback(() => {
    if (path === null) return;
    inFlight.current?.abort();
    const abort = new AbortController();
    inFlight.current = abort;
    setChecking(true);
    readJson(path, schema, abort.signal)
      .then((data) => {
        if (!abort.signal.aborted) setHeld({ of: path, state: { status: 'ok', data } });
      })
      .catch((error: unknown) => {
        if (abort.signal.aborted) return;
        setHeld({
          of: path,
          state: { status: 'error', message: error instanceof Error ? error.message : 'Failed' },
        });
      })
      .finally(() => {
        if (!abort.signal.aborted) setChecking(false);
      });
  }, [path, schema]);

  useEffect(() => {
    refresh();
    return () => inFlight.current?.abort();
  }, [refresh]);

  useWhileVisible(refresh, path !== null && every !== undefined, every);

  return { state: held.of === path ? held.state : LOADING, checking, refresh };
}

/** With `every`, coming back into view asks only once `every` has passed since the last ask. */
export function useWhileVisible(run: () => void, on: boolean, every?: number): void {
  useEffect(() => {
    if (!on) return;
    let last = Date.now();
    const seen = () => {
      if (document.visibilityState !== 'visible') return;
      if (every !== undefined && Date.now() - last < every) return;
      last = Date.now();
      run();
    };
    const timer = every === undefined ? undefined : setInterval(seen, every);
    document.addEventListener('visibilitychange', seen);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', seen);
    };
  }, [run, on, every]);
}

export interface FreshRead<T> {
  answer: T;
  checking: boolean;
  refresh: () => void;
}

// Each of these reads asks Kite, so a tab flicking in and out of view does not.
const FRESH_READ_MS = 60_000;

/** Read on mount, then every minute while the tab is in view. */
export function useFreshRead<T>(
  ask: boolean,
  path: string,
  schema: Parser<T>,
  fallback: T,
  { keepOnFail = false, every = FRESH_READ_MS }: { keepOnFail?: boolean; every?: number } = {},
): FreshRead<T> {
  const { state, checking, refresh } = useRead(ask ? path : null, schema, { every });
  const kept = useRef(fallback);
  if (state.status === 'ok') kept.current = state.data;
  else if (state.status === 'loading') kept.current = fallback;
  const failed = keepOnFail ? kept.current : fallback;
  return { answer: state.status === 'ok' ? state.data : failed, checking, refresh };
}

// Debounced; answers for stale keys are dropped and the last answer stays until
// the next lands, so the panel never flickers empty. An empty key asks nothing.
export function useLatest<T>(
  key: string,
  ms: number,
  ask: (key: string) => Promise<T>,
): { key: string; data: T } | null {
  const [answer, setAnswer] = useState<{ key: string; data: T } | null>(null);
  // A ref, so an inline `ask` closure does not re-ask an unchanged key.
  const askRef = useRef(ask);
  askRef.current = ask;
  useEffect(() => {
    if (key === '') return;
    let live = true;
    const timer = setTimeout(() => {
      askRef
        .current(key)
        .then((data) => {
          if (live) setAnswer({ key, data });
        })
        .catch(() => undefined);
    }, ms);
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [key, ms]);
  return answer;
}

export function useDebounced<T>(value: T, ms: number): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return settled;
}

export const useAnalysts = (ask = true): Read<AnalystsReply> =>
  useRead(ask ? API_PATHS.analysts : null, analystsReplySchema);
